import { useEffect, useState } from 'react';
import { OrderByInput, useCustomQuery } from '../graphql';
import { ColumnDef } from '@tanstack/react-table';

export type UseDataGridExportProps = {
  query: any;
  filter: any;
  orderBy: OrderByInput;
  dataPrefix: string;
  columns: ColumnDef<any, any>[];
  download: boolean;
  onDataReady: () => void;
};

type ExportHeader = {
  key: string;
  label: string;
};

export const useExportAssets = (props: UseDataGridExportProps) => {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState<ExportHeader[]>([]);

  useEffect(() => {
    setHeaders(
      props.columns
        .filter((column) => (column as any).accessorKey !== 'selected')
        .filter((column) => (column as any).meta?.exportable !== false) // Only exclude columns explicitly marked as not exportable
        .map((column) => ({
          key: (column as any)?.accessorKey,
          label: (column as any).meta?.exportHeader || 
                 (typeof (column as any).header === 'function' ? (column as any).header() : (column as any).header) || 
                 (column as any).accessorKey || '',
        }))
    );
  }, [props.columns]);

  const { data: exportData, isLoading } = useCustomQuery({
    query: props.query,
    cacheKey: [],
    variables: {
      filter: props.filter,
      orderBy: props.orderBy,
      page: { limit: 10000, offset: 0 },
    },
    options: {
      enabled: props.download,
      gcTime: 0,
      staleTime: 0,
    },
  });

  useEffect(() => {
    if (!exportData || !props.download) return;

    const exportRows = props.dataPrefix
      .split('.')
      .reduce((acc, part) => acc && acc[part], exportData)
      .items.map((item: any) => {
        const row = {};

        headers.forEach((header) => {
          const exporter = (props.columns.find(
            (col) => (col as any).accessorKey === header.key
          ) as any)?.meta?.exportCell;

          (row as any)[header.key] = exporter ? exporter(item) : (item as any)[header.key];
        });

        return row;
      });

    setRows(exportRows);
    props.onDataReady();
  }, [exportData, props.download]);

  return {
    headers,
    rows,
    isLoading,
  };
};
